import React, { useCallback, useRef, useState } from 'react';

/**
 * RedactorFileUpload – drag-and-drop / browse file selector.
 * Accepts PDF, DOCX, and TXT files (up to 20).
 */
const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'application/json',
  'text/xml',
  'application/xml',
  'application/sql',
];
const ACCEPTED_EXT = ['.pdf', '.docx', '.txt', '.sql', '.csv', '.psql', '.json', '.xml', '.md'];
const MAX_FILES = 20;

function RedactorFileUpload({ files, setFiles, disabled }) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback(
    (incoming) => {
      const valid = Array.from(incoming).filter((f) => {
        const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
        return ACCEPTED_EXT.includes(ext);
      });
      setFiles((prev) => {
        const combined = [...prev, ...valid];
        return combined.slice(0, MAX_FILES);
      });
    },
    [setFiles]
  );

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleBrowse = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="mb-4">
      <h5 className="mb-3">
        <i className="bi bi-cloud-upload me-2"></i>Upload Files
      </h5>

      <div
        className={`border rounded p-4 text-center ${dragActive ? 'border-primary bg-primary bg-opacity-10' : 'border-secondary'}`}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer', minHeight: 140 }}
        onDrop={disabled ? undefined : handleDrop}
        onDragOver={disabled ? undefined : handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <i className="bi bi-file-earmark-arrow-up fs-1 text-secondary"></i>
        <p className="mt-2 mb-1">
          Drag &amp; drop files here, or <span className="text-primary">browse</span>
        </p>
        <small className="text-muted">
          PDF, DOCX, TXT, SQL, CSV, PSQL, JSON, XML, MD &middot; Up to {MAX_FILES} files &middot; 50 MB each
        </small>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXT.join(',')}
          className="d-none"
          onChange={handleBrowse}
          disabled={disabled}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <small className="text-muted">{files.length} file(s) selected</small>
            {!disabled && (
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => setFiles([])}
              >
                Clear all
              </button>
            )}
          </div>
          <ul className="list-group list-group-flush" style={{ maxHeight: 260, overflowY: 'auto' }}>
            {files.map((f, i) => (
              <li key={i} className="list-group-item d-flex justify-content-between align-items-center py-2 px-3">
                <div className="text-truncate me-3" style={{ maxWidth: '70%' }}>
                  <i className={`bi ${f.name.endsWith('.pdf') ? 'bi-file-earmark-pdf text-danger' : f.name.endsWith('.docx') ? 'bi-file-earmark-word text-primary' : f.name.endsWith('.json') ? 'bi-filetype-json text-warning' : f.name.endsWith('.xml') ? 'bi-filetype-xml text-success' : 'bi-file-earmark-text text-secondary'} me-2`}></i>
                  {f.name}
                </div>
                <div className="d-flex align-items-center">
                  <small className="text-muted me-3">{formatSize(f.size)}</small>
                  {!disabled && (
                    <button
                      className="btn btn-sm btn-outline-secondary border-0"
                      onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default RedactorFileUpload;
